import { useEffect, useState } from 'react';
import { Box, TableContainer, Text } from '@chakra-ui/react';
import CustomChakraButton from '../../../../CustomChakraButton/CustomChakraButton';
import { HelperBox } from './HelperBox';
import { PendingChildrenTable } from './PendingChildrenTable';
import { AxiosResponse, HttpStatusCode } from 'axios';
import { useApi } from '../../../../../context/ApiProvider';
import { useParams } from 'react-router-dom';
import { ConsumerCandidateInterface } from '../../../../../shared/type/consumers-candidates.type';
import { EditPendingKidPopup } from '../EditPendingKidPopup';
import { useAlertContext } from '../../../../../context/AlertProvider';
import IconComponent from '../../../../IconComponent/IconComponent';

export const WaitingRoom = () => {
  const { id: tenantId } = useParams();
  const { apiTenantConsumerCandidateController } = useApi();
  const { setShow, setAlertProperties } = useAlertContext();
  const [showHelperBox, setShowHelperBox] = useState(true);
  const [pendingChildren, setPendingChildren] = useState<ConsumerCandidateInterface[] | null>(null);
  const [selectedKidIds, setSelectedKidIds] = useState<number[]>([]);
  const [showEditPendingKidPopup, setShowEditPendingKidPopup] = useState(false);
  const selectedKidToEdit =
    selectedKidIds && selectedKidIds.length < 2
      ? pendingChildren?.find((kid) => selectedKidIds.includes(kid.candidateData.id))
      : null;

  const fetchPendingChildren = async () => {
    if (tenantId === undefined) {
      return;
    }
    try {
      const response: AxiosResponse<ConsumerCandidateInterface[]> =
        await apiTenantConsumerCandidateController('').get(`${tenantId}/list`);
      setPendingChildren(response.data);
    } catch (error) {
      console.error('Failed to fetch consumer candidates list:', error);
    }
  };

  const handleAcceptKid = async (kidId: number) => {
    if (kidId === undefined) {
      return;
    }
    try {
      const response: AxiosResponse<ConsumerCandidateInterface[]> =
        await apiTenantConsumerCandidateController('').post(`${kidId}/accept`);
      if (response.status === HttpStatusCode.Ok) {
        setAlertProperties({
          timeout: 9000,
          title: 'Uczeń został poprawnie zaakceptowany',
          status: 'success'
        });
      }
    } catch (error) {
      console.error('Failed to accept consumer candidates', error);
      setAlertProperties({
        timeout: 9000,
        title: 'Uczeń nie został zaakceptowany',
        status: 'error'
      });
    } finally {
      setShow(true);
    }
  };

  const handleRejectKid = async (kidId: number) => {
    if (kidId === undefined) {
      return;
    }
    try {
      const response: AxiosResponse<ConsumerCandidateInterface[]> =
        await apiTenantConsumerCandidateController('').post(`${kidId}/reject`);

      if (response.status === HttpStatusCode.Ok) {
        setAlertProperties({
          timeout: 9000,
          title: 'Uczeń został odrzucony',
          status: 'success'
        });
      }
    } catch (error) {
      console.error('Failed to accept consumer candidates', error);
      setAlertProperties({
        timeout: 9000,
        title: 'Uczeń nie został odrzucony',
        status: 'error'
      });
    } finally {
      setShow(true);
    }
  };

  const allKidsIds = pendingChildren?.flatMap((kid) => kid.candidateData.id) || [];

  const toggleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedKidIds(allKidsIds);
    } else {
      setSelectedKidIds([]);
    }
  };

  const areAllSelected = allKidsIds.length > 0 && selectedKidIds.length === allKidsIds.length;

  const toggleSingleKid = (kidId: number, checked: boolean) => {
    setSelectedKidIds((prev) => (checked ? [...prev, kidId] : prev.filter((id) => id !== kidId)));
  };

  const onCloseEditPendingKidPopup = () => {
    setShowEditPendingKidPopup(false);
    setSelectedKidIds([]);
    fetchPendingChildren();
  };

  useEffect(() => {
    fetchPendingChildren();
  }, [tenantId]);

  return (
    <section className="py-4 px-6 w-full">
      <div className="mb-8">
        <div className="w-full flex flex-col gap-4 sm:gap-0 sm:flex-row items-start sm:items-center justify-between">
          <div className="flex flex-col basis-5/12">
            <Box>
              <Text
                whiteSpace="normal"
                overflow="hidden"
                textOverflow="ellipsis"
                className="font-semibold text-base normal-case text-grayLight-900">
                Poczekalnia
              </Text>
              <Text
                whiteSpace="normal"
                overflow="hidden"
                textOverflow="ellipsis"
                className="font-normal text-sm normal-case text-grayLight-700">
                Poniżej znajdują się konta uczniów oczekujące na akceptację lub przypisanie do konta
                rodzica.
              </Text>
            </Box>
          </div>
          <div className="flex gap-4 items-center">
            {!showHelperBox && (
              <CustomChakraButton
                hierarchy="linkGray"
                size="md"
                icon="question"
                iconPosition="left"
                buttonProps={{
                  onClick: () => setShowHelperBox(true)
                }}>
                Jak to działa?
              </CustomChakraButton>
            )}
            <CustomChakraButton
              hierarchy="linkGray"
              size="md"
              iconPosition="onlyText"
              buttonProps={{
                onClick: () => {
                  return setShowEditPendingKidPopup(true);
                },
                disabled: !selectedKidToEdit || selectedKidIds.length !== 1
              }}>
              Edytuj dane
            </CustomChakraButton>
            <CustomChakraButton
              hierarchy="destructiveSecondary"
              size="md"
              iconPosition="left"
              icon="xClose"
              iconColor={!selectedKidToEdit || selectedKidIds.length === 0 ? '' : '#b42318'}
              buttonProps={{
                onClick: async () => {
                  if (selectedKidIds.length === 0) {
                    return;
                  }

                  try {
                    await Promise.all(selectedKidIds.map((kidId) => handleRejectKid(kidId)));
                    setSelectedKidIds([]);
                    fetchPendingChildren();
                  } catch (error) {
                    console.error('Error while accepting kids', error);
                  }
                },
                disabled: !selectedKidIds || selectedKidIds.length === 0
              }}>
              Odrzuć
            </CustomChakraButton>
            <CustomChakraButton
              hierarchy="primary"
              size="md"
              iconPosition="left"
              icon="check"
              buttonProps={{
                onClick: async () => {
                  if (selectedKidIds.length === 0) {
                    return;
                  }

                  try {
                    await Promise.all(selectedKidIds.map((kidId) => handleAcceptKid(kidId)));
                    setSelectedKidIds([]);
                    fetchPendingChildren();
                  } catch (error) {
                    console.error('Error while accepting kids', error);
                  }
                },
                disabled: !selectedKidIds || selectedKidIds.length === 0
              }}>
              Akceptuj
            </CustomChakraButton>
          </div>
        </div>
      </div>

      {showHelperBox && <HelperBox onHide={() => setShowHelperBox(false)} />}

      <div className="mt-spacing-xl">
        {pendingChildren && (
          <TableContainer overflowY="auto">
            <PendingChildrenTable
              isLoading={false}
              pendingChildren={pendingChildren}
              areAllSelected={areAllSelected}
              toggleSingleKid={toggleSingleKid}
              toggleSelectAll={toggleSelectAll}
              selectedKidIds={selectedKidIds}
              afterAction={fetchPendingChildren}
            />
          </TableContainer>
        )}
      </div>
      {showEditPendingKidPopup && (
        <EditPendingKidPopup
          isOpen={showEditPendingKidPopup}
          pendingKid={selectedKidToEdit}
          onClose={onCloseEditPendingKidPopup}
        />
      )}
    </section>
  );
};
